/** Sprint lifecycle stored on `sprints` documents. */
export const SPRINT_STATUSES = [
  { id: 'planned', label: 'Planned' },
  { id: 'active', label: 'Active' },
  { id: 'completed', label: 'Completed' },
];

export const DEFAULT_SPRINT_STATUS = 'planned';

/**
 * @param {import('firebase/firestore').Timestamp | Date | string | null | undefined} value
 * @returns {Date | null}
 */
export function sprintDateToJs(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * @param {import('firebase/firestore').Timestamp | Date | string | null | undefined} value
 * @returns {string}
 */
export function formatSprintDate(value) {
  const d = sprintDateToJs(value);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * @param {Record<string, unknown> | null | undefined} sprint
 * @returns {string}
 */
export function sprintStatusLabel(sprint) {
  const id = typeof sprint?.status === 'string' ? sprint.status : DEFAULT_SPRINT_STATUS;
  return SPRINT_STATUSES.find((s) => s.id === id)?.label || id;
}

/**
 * Pick the sprint the Kanban board should open on.
 * @param {Array<Record<string, unknown> & { id: string }>} sprints
 * @returns {(Record<string, unknown> & { id: string }) | null}
 */
export function resolveActiveSprint(sprints) {
  if (!Array.isArray(sprints) || sprints.length === 0) return null;

  const explicit = sprints.find((s) => s.status === 'active');
  if (explicit) return explicit;

  const now = Date.now();
  const inRange = sprints.find((s) => {
    const start = sprintDateToJs(s.startDate);
    const end = sprintDateToJs(s.endDate);
    if (!start || !end) return false;
    return now >= start.getTime() && now <= end.getTime();
  });
  if (inRange) return inRange;

  const planned = sprints
    .filter((s) => s.status === 'planned')
    .sort((a, b) => {
      const aStart = sprintDateToJs(a.startDate)?.getTime() ?? Infinity;
      const bStart = sprintDateToJs(b.startDate)?.getTime() ?? Infinity;
      return aStart - bStart;
    });
  if (planned.length > 0) return planned[0];

  return sprints[0];
}

/**
 * @param {string} sprintId
 * @param {Array<{ id: string, name?: string, status?: string }>} sprints
 * @returns {string}
 */
export function sprintSelectLabel(sprintId, sprints) {
  const sprint = sprints.find((s) => s.id === sprintId);
  if (!sprint) return 'Select sprint';
  const name = typeof sprint.name === 'string' ? sprint.name : 'Sprint';
  const status = sprint.status === 'active' ? ' · Active' : '';
  return `${name}${status}`;
}
